import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Equipment from "@/pages/Equipment";
import Engineers from "@/pages/Engineers";
import Inventory from "@/pages/Inventory";
import AMC from "@/pages/AMC";
import PMSchedules from "@/pages/PMSchedules";
import Invoices from "@/pages/Invoices";
import Reports from "@/pages/Reports";
import UsersPage from "@/pages/Users";
import Documents from "@/pages/Documents";
import Quotations from "@/pages/Quotations";
import VendorWarranty from "@/pages/VendorWarranty";
import Expenses from "@/pages/Expenses";
import SpareMaster from "@/pages/SpareMaster";
import VendorDailyOrder from "@/pages/VendorDailyOrder";
import DeliveryChallans from "@/pages/DeliveryChallans";
import Settings from "@/pages/Settings";
import Reminders from "@/pages/Reminders";
import OfferLetters from "@/pages/OfferLetters";
import MarginCalculator from "@/pages/MarginCalculator";
import ExperienceLetters from "@/pages/ExperienceLetters";
import ChequeEntry from "@/pages/ChequeEntry";
import Attendance from "@/pages/Attendance";

import EngineerLayout from "@/components/EngineerLayout";
import EngineerHome from "@/pages/EngineerHome";
import EngineerJob from "@/pages/EngineerJob";
import EngineerHistory from "@/pages/EngineerHistory";
import EngineerProfile from "@/pages/EngineerProfile";

import CustomerPortal from "@/pages/CustomerPortal";

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "engineer") return <Navigate to="/engineer" replace />;
  if (user.role === "customer") return <Navigate to="/portal" replace />;
  return <Navigate to="/app/dashboard" replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Staff console */}
            <Route
              path="/app"
              element={
                <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher"]}>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="tickets/:id" element={<TicketDetail />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="equipment" element={<Equipment />} />
              <Route path="engineers" element={<Engineers />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="amc" element={<AMC />} />
              <Route path="pm" element={<PMSchedules />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="reports" element={<Reports />} />
              <Route path="documents" element={<Documents />} />
              <Route path="quotations" element={<Quotations />} />
              <Route path="warranty" element={<VendorWarranty />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="spares" element={<SpareMaster />} />
              <Route path="vendor-orders" element={<VendorDailyOrder />} />
              <Route path="delivery-challans" element={<DeliveryChallans />} />
              <Route path="settings" element={<Settings />} />
              <Route path="reminders" element={<Reminders />} />
              <Route path="offer-letters" element={<OfferLetters />} />
              <Route path="margin-calculator" element={<MarginCalculator />} />
              <Route path="experience-letters" element={<ExperienceLetters />} />
              <Route path="cheques" element={<ChequeEntry />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="users" element={<UsersPage />} />
            </Route>

            {/* Engineer mobile portal */}
            <Route
              path="/engineer"
              element={
                <ProtectedRoute roles={["engineer"]}>
                  <EngineerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<EngineerHome />} />
              <Route path="history" element={<EngineerHistory />} />
              <Route path="profile" element={<EngineerProfile />} />
            </Route>
            <Route
              path="/engineer/job/:id"
              element={
                <ProtectedRoute roles={["engineer"]}>
                  <EngineerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<EngineerJob />} />
            </Route>

            {/* Customer portal */}
            <Route
              path="/portal"
              element={
                <ProtectedRoute roles={["customer"]}>
                  <CustomerPortal />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
